import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    FormControlLabel,
    InputAdornment,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import AccountApiService from '../../../api/AccountApiService';
import AppDialog from '../AppDialog';
import * as Constants from '../../../utilities/Constants';

const CREDIT = 'credit';
const CHARGE = 'charge';

/**
 * Corrects a tenant's balance.
 *
 * The ledger is append-only, so nothing here rewrites history: a correction is another line, with
 * the owner's reason attached, and the tenant sees it on their statement alongside everything else.
 * That is deliberate - a balance that can be silently edited is not worth showing anyone.
 *
 * Direction is a pair of radio buttons rather than a signed number in the box. Typing a minus sign
 * to mean "they owe less" is easy to get backwards, and getting it backwards here doubles a debt
 * instead of clearing it.
 */
export default function LedgerAdjustmentModal(props) {
    const { open, close, account, setSnackbarProperties, onAdjusted } = props;

    const [direction, setDirection] = useState(CREDIT);
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const accountApiService = new AccountApiService();

    const parsedAmount = Number.parseFloat(amount);
    const amountIsUsable = Number.isFinite(parsedAmount) && parsedAmount > 0;
    const signedAmount = direction === CREDIT ? -parsedAmount : parsedAmount;

    async function handleSave() {
        setSaving(true);
        setError('');

        const response = await accountApiService.postAdjustment({
            appUserId: account.appUserId,
            amount: signedAmount,
            description: description.trim(),
        });

        setSaving(false);

        if (!response.success) {
            setError(response.message);

            return;
        }

        setSnackbarProperties(`Adjusted ${account.tenantName}'s balance`);
        closeModal();
        onAdjusted();
    }

    function closeModal() {
        setDirection(CREDIT);
        setAmount('');
        setDescription('');
        setError('');
        close();
    }

    useEffect(() => {
        if (open) {
            setDirection(CREDIT);
            setAmount('');
            setDescription('');
            setError('');
        }
    }, [open]);

    if (!account) {
        return null;
    }

    const projectedBalance = amountIsUsable ? account.balance + signedAmount : account.balance;

    return (
        <AppDialog
            open={open}
            onClose={closeModal}
            title={`Adjust ${account.tenantName}'s balance`}
            onSubmit={handleSave}
            submitText={'Post adjustment'}
            submitDisabled={!amountIsUsable || description.trim().length === 0}
            busy={saving}
        >
            <Stack spacing={2}>
                <Box>
                    <Typography variant={'body2'} sx={{ color: 'text.secondary' }}>
                        Current balance
                    </Typography>
                    <Typography variant={'h6'}>
                        {Constants.USDollar.format(account.balance)}
                    </Typography>
                </Box>

                {error && <Alert severity={'error'}>{error}</Alert>}

                <RadioGroup value={direction} onChange={(event) => setDirection(event.target.value)}>
                    <FormControlLabel
                        value={CREDIT}
                        control={<Radio />}
                        label={'Credit the tenant (they owe less)'}
                    />
                    <FormControlLabel
                        value={CHARGE}
                        control={<Radio />}
                        label={'Charge the tenant (they owe more)'}
                    />
                </RadioGroup>

                <TextField
                    label={'Amount'}
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    type={'number'}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputProps={{ startAdornment: <InputAdornment position={'start'}>$</InputAdornment> }}
                    fullWidth
                />

                <TextField
                    label={'Reason'}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    helperText={'Shown to the tenant on their statement.'}
                    multiline
                    minRows={2}
                    fullWidth
                />

                {amountIsUsable &&
                    <Alert severity={'info'}>
                        {`Their balance will become ${Constants.USDollar.format(projectedBalance)}.`}
                    </Alert>
                }
            </Stack>
        </AppDialog>
    );
}

LedgerAdjustmentModal.propTypes = {
    open: PropTypes.bool.isRequired,
    close: PropTypes.func.isRequired,

    /** The account being corrected, or null when the dialog is closed. */
    account: PropTypes.shape({
        appUserId: PropTypes.number,
        tenantName: PropTypes.string,
        balance: PropTypes.number,
    }),

    setSnackbarProperties: PropTypes.func.isRequired,

    /** Called after a successful adjustment so the caller can reload. */
    onAdjusted: PropTypes.func.isRequired,
};
